import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { SectorBar } from "./FeatureCard";

/**
 * Sleek, motorsport "timing board" stack of collapsible feature panels.
 * - Multiple panels can be open at once.
 * - Panels auto-expand as they scroll into the viewport's center band.
 * - Any header can be clicked to expand / collapse that panel.
 * Stacks vertically so it reads the same on desktop and phone browsers.
 */
export function FeatureStack({ items, defaultOpen = [0], testId, tabBadge = false, largeBody = false }) {
  const [open, setOpen] = useState(() => new Set(defaultOpen));
  const refs = useRef([]);
  const dismissed = useRef(new Set());

  const toggle = useCallback((i) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
        dismissed.current.add(i); // remember manual collapse so scroll won't re-open it
      } else {
        next.add(i);
        dismissed.current.delete(i);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = Number(entry.target.dataset.idx);
          if (dismissed.current.has(idx)) return; // respect manual collapse
          setOpen((prev) => {
            if (prev.has(idx)) return prev;
            const next = new Set(prev);
            next.add(idx);
            return next;
          });
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.filter(Boolean).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items.length]);

  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((item, i) => {
        const isOpen = open.has(i);
        return (
          <article
            key={i}
            ref={(el) => (refs.current[i] = el)}
            data-idx={i}
            data-testid={`feature-panel-${i}`}
            data-open={isOpen}
            className={`group relative rounded-xl border bg-card transition-colors duration-500 ${tabBadge ? "overflow-visible" : "overflow-hidden"} ${
              isOpen ? "border-primary/50 shadow-[0_20px_50px_-30px_hsl(var(--primary)/0.5)]" : "border-white/15 hover:border-primary/40"
            }`}
          >
            <span
              aria-hidden
              className={`absolute left-0 w-[3px] bg-primary transition-opacity duration-500 ${tabBadge ? "top-3 bottom-3 rounded-full" : "top-0 h-full"} ${isOpen ? "opacity-100" : "opacity-0"}`}
            />
            <span
              aria-hidden
              className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-primary/70 via-primary/20 to-transparent transition-opacity duration-500 ${isOpen ? "opacity-100" : "opacity-0"}`}
            />
            <button
              type="button"
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
              data-testid={`feature-panel-toggle-${i}`}
              className={`flex w-full items-start gap-3 px-5 py-4 text-left sm:items-center sm:gap-4 sm:py-5 sm:px-7 ${tabBadge ? "relative" : ""}`}
            >
              {tabBadge ? (
                <span className="absolute -left-10 top-0 h-[3rem] z-20 flex w-[3.6rem] -skew-x-[11deg] items-center justify-center overflow-hidden bg-white shadow-[0_8px_18px_-6px_rgba(0,0,0,0.6)] sm:-left-14 sm:bottom-0 sm:h-auto sm:w-[4.75rem]">
                  <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 bg-primary" />
                  <span className="skew-x-[11deg] font-display text-[1.3rem] font-black italic leading-none tabular-nums text-black sm:text-[1.7rem]">
                    {item.pos}
                  </span>
                </span>
              ) : (
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary font-mono text-xs font-bold tabular-nums text-primary-foreground shadow-[0_0_14px_-3px_hsl(var(--primary)/0.8)]">
                  {item.pos}
                </span>
              )}
              <span className={`min-w-0 flex-1 ${tabBadge ? "pl-2 sm:pl-5" : ""}`}>
                <span className="mt-0.5 block font-display text-[1.08rem] font-bold uppercase leading-tight sm:text-[1.5rem]">
                  {item.title}
                </span>
                {!isOpen && item.summary && (
                  <span className={`mt-1 block leading-snug text-muted-foreground ${largeBody ? "text-[0.74rem] sm:text-[0.825rem]" : "text-[0.675rem] sm:text-xs"}`}>{item.summary}</span>
                )}
              </span>
              <span className="hidden flex-shrink-0 sm:block">
                <SectorBar i={i} />
              </span>
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                  isOpen ? "rotate-180 border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground"
                }`}
              >
                <ChevronDown className="h-4 w-4" />
              </span>
            </button>
            <div
              className={`grid transition-all duration-500 ease-out ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="border-t border-white/15 px-5 pb-7 pt-6 sm:px-7">{item.content}</div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
